// Per-agent metadata (admin JWT for the customer surface; operator JWT for the
// back office). Shape mirrors the API exactly (api AgentSummary):
//   - GET /admin/agents               → this org's live agents (grouped in the UI
//     by agent.sessionId === channel.relaySessionId)
//   - GET /operator/orgs/:id/agents   → any org's live agents (operator client)
//
// GRACEFUL: the API degrades to { agents: [] } when the relay link is off or a
// blip occurs — never a hard error — so both surfaces just render "no agents".
import { z } from "zod";
import { apiFetch } from "./client";

export interface AgentSummary {
  sessionId: string;
  peerId: string;
  platform: string | null;
  type: string | null;
  transport: string | null;
  clientVersion: string | null;
  cwd: string | null;
  label: string | null;
  registeredAt: string | null;
  lastSeenAt: string | null;
}

export const agentSummarySchema = z.object({
  sessionId: z.string(),
  peerId: z.string(),
  platform: z.string().nullable(),
  type: z.string().nullable(),
  transport: z.string().nullable(),
  clientVersion: z.string().nullable(),
  cwd: z.string().nullable(),
  label: z.string().nullable(),
  registeredAt: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
}) satisfies z.ZodType<AgentSummary>;

export const agentsResponseSchema = z.object({ agents: z.array(agentSummarySchema) });

/** Platform code → display label. cc = Claude Code, codex = Codex; others fall back. */
export function platformLabel(platform: string | null): string {
  switch (platform) {
    case "cc":
      return "Claude";
    case "codex":
      return "Codex";
    case "slack":
      return "Slack";
    case "gchat":
      return "Google Chat";
    case "web":
      return "Web";
    default:
      return platform ?? "Agent";
  }
}

/** Customer: live agents connected to this org's Team channels (admin-gated). */
export async function listChannelAgents(signal?: AbortSignal): Promise<AgentSummary[]> {
  const res = await apiFetch("/admin/agents", { auth: true, signal, schema: agentsResponseSchema });
  return res.agents;
}
